import { Rental } from '@/services/types';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { RentalStatusBadge } from './components/RentalStatusBadge';
import { Button } from '@/components/ui/button';
import { FileSymlink, FileText, Pencil, Trash2, CircleDollarSign, Route } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export const getRentalColumns = (
  onViewBike?: (bikeId: string) => void,
  onEditRental?: (rental: Rental) => void,
  onDeleteRental?: (rental: Rental) => void
) => [
  {
    key: 'human_number',
    header: '№ аренды',
    cell: (rental: Rental) => {
      return rental.human_number ? (
        <span className="font-medium text-primary">{rental.human_number}</span>
      ) : (
        rental.id.substring(0, 8)
      );
    },
    sortable: true
  },
  {
    key: 'bikeId',
    header: 'Велосипед',
    cell: (rental: Rental) => (
      <div className="flex items-center space-x-1">
        <span>{rental.bikeName || rental.bikeId.substring(0, 8)}</span>
        {onViewBike && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2"
            onClick={(e) => {
              e.stopPropagation();
              onViewBike(rental.bikeId);
            }}
            title="Детали велосипеда"
          >
            <FileSymlink className="h-3 w-3" />
          </Button>
        )}
      </div>
    ),
    sortable: true
  },
  {
    key: 'courierName',
    header: 'Курьер',
    cell: (rental: Rental) => rental.courierName,
    sortable: true
  },
  {
    key: 'startDate',
    header: 'Дата начала',
    cell: (rental: Rental) => {
      try {
        return format(new Date(rental.startDate), 'dd.MM.yyyy', { locale: ru });
      } catch (e) {
        return 'Некорректная дата';
      }
    },
    sortable: true
  },
  {
    key: 'endDate',
    header: 'Дата окончания',
    cell: (rental: Rental) => {
      if (!rental.endDate) {
        return '—';
      }
      try {
        return format(new Date(rental.endDate), 'dd.MM.yyyy', { locale: ru });
      } catch (e) {
        return 'Некорректная дата';
      }
    },
    sortable: true
  },
  {
    key: 'total_mileage',
    header: 'Пробег',
    cell: (rental: Rental) => {
      const mileage = rental.total_mileage || 0;
      return (
        <div className="flex items-center gap-1">
          <Route className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm">
            {mileage > 0 ? `${mileage.toFixed(2)} км` : '—'}
          </span>
        </div>
      );
    },
    sortable: true
  },
  {
    key: 'cost',
    header: 'Стоимость',
    cell: (rental: Rental) => `${rental.cost} ₽`,
    sortable: true
  },
  {
    key: 'subscription',
    header: 'Подписка',
    cell: (rental: Rental) => {
      if (rental.subscription_id) {
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <CircleDollarSign className="h-3 w-3" />
            <span>Активна</span>
          </Badge>
        );
      }
      return null;
    },
    sortable: true
  },
  {
    key: 'status',
    header: 'Статус',
    cell: (rental: Rental) => <RentalStatusBadge status={rental.status} className="flex items-center" />,
    sortable: true
  },
  {
    key: 'documentsUrl',
    header: 'Документы',
    cell: (rental: Rental) => {
      // Используем только прямую ссылку из поля documentsUrl
      return rental.documentsUrl ? (
        <a 
          href={rental.documentsUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="flex items-center text-primary hover:text-primary-dark"
          onClick={(e) => e.stopPropagation()}
        >
          <FileText className="h-4 w-4 mr-1" />
          <span>Акт</span>
        </a>
      ) : null
    },
    sortable: false
  },
  {
    key: 'actions',
    header: 'Действия',
    cell: (rental: Rental) => (
      <div className="flex space-x-2">
        {onEditRental && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onEditRental(rental);
            }}
            title="Редактировать"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        {onDeleteRental && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteRental(rental);
            }}
            title="Удалить"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
    ),
    sortable: false
  }
];
